import { Box, CssBaseline, useMediaQuery } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/core/styles";
import { useEffect, useState } from "react";
import theme from "./utils/theme";
import "./App.css";
import Header from "./components/Header/Header";
import Content from "./components/Content/Content";
import Footer from "./components/Footer/Footer";
import Axios from "axios";
import { getUrlParams } from "./utils/getUrlParams";
import FirstPage from "./components/FirstPage/FirstPage";
import { getCookie, setCookie } from "./utils/cookies";
import { supportedLanguages } from ".";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import * as Sentry from "@sentry/react";
import ErrorBoundaryFallback from "./components/ErrorBoundaryFallback/ErrorBoundaryFallback";

import common_en from "./english.json";
import common_nl from "./dutch.json";
import common_de from "./german.json";

const currentLng = getCookie("lng") || undefined;

i18n
  .use(initReactI18next) // passes i18n down to react-i18next
  .init({
    // the translations
    // (tip move them in a JSON file and import them,
    // or even better, manage them via a UI: https://react.i18next.com/guides/multiple-translation-files#manage-your-translations-with-a-management-gui)
    defaultNS: "translations",
    ns: "translations",
    resources: {
      en: {
        translations: common_en
      },
      nl: {
        translations: common_nl
      },
      de: {
        translations: common_de
      }
    },
    lng: currentLng, // if you're using a language detector, do not define the lng option
    fallbackLng: "en",

    interpolation: {
      escapeValue: false // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
    }
  });

export const checkCountry = async () => {
  const res = await Axios.get("https://www.cloudflare.com/cdn-cgi/trace")
  const data = res.data
    .split("\n")
    .find((line: string) => line.includes("loc"));
  return data.slice(4);
};

const checkIsLanguageSupported = (lng: string) => {
  return supportedLanguages.find((lngInfo) => {
    return lngInfo.includes(lng.toLowerCase());
  });
}

let interval: any;
function App() {
  const screenSm: boolean = useMediaQuery(theme.breakpoints.down("md"));

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const [data, setData] = useState<any>({});

  let { tracking_number, postal_code, serviceId } = getUrlParams();
  const [firstPage, setFirstPage] = useState(!tracking_number && !serviceId);
  const [inputValues, setInputValues] = useState({
    trackingNumber: tracking_number,
    postalCode: postal_code || ""
    // stopId: ""
  });

  const [postalAvailable, setPostalAvailable] = useState<boolean>(false);

  const [error, setError] = useState<string | undefined>("");
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const [language, setLanguage] = useState<string>(getCookie("lng") || "en");

  const changeLanguage = (lng?: string) => {
    const newLng = lng || window.navigator.language.toLowerCase().slice(0, 2) || "en";
    i18n.changeLanguage(newLng);
    setLanguage(newLng);
    setCookie("lng", newLng);
  };

  useEffect(() => {
    const currentLng = getCookie("lng");
    if (!currentLng) {
      checkCountry()
        .then((res: string) => {
          if (
            checkIsLanguageSupported(res)
          ) {
            // if country is Austria, change language to German
            if(res?.toLowerCase().includes("at")) {
              changeLanguage("de");
            }
            changeLanguage(res.toLowerCase());
          }
          else {
            changeLanguage();
          }
        })
        .catch(e => {
          changeLanguage();
        });
    }
  }, []);

  useEffect(() => {
    let firstFetch = true;
    setDataLoaded(false);
    function fetchData() {
      if (!inputValues.trackingNumber && !serviceId) {
        if (interval) clearInterval(interval);
        // setError("Please use a link which contains service details");
        return;
      }
      const url = inputValues.trackingNumber
        ? `${process.env.REACT_APP_API_URL}/v1/tracking?tracking_number=${inputValues.trackingNumber}&postal_code=${inputValues.postalCode}`
        : `${process.env.REACT_APP_API_URL}/v1/tracking?stop_id=${serviceId}&postal_code=${inputValues.postalCode}`;

      Axios.get(url)
        .then(res => {
          console.log(dataLoaded, inputValues.postalCode);
          if (
            firstFetch &&
            !inputValues.postalCode &&
            res.data.postal_available
          ) {
            setTooltipOpen(true);
            firstFetch = false;
          }
          console.log(res.data);
          setData(res.data);
          setError("");

          setPostalAvailable(res.data.postal_available);
          // res.data.polyline && setPolyline(decoder.decode(res.data.polyline));
        })
        .catch(err => {
          if (err?.response?.data?.message) {
            setError(err.response.data.message);
          } else {
            setError("Something went wrong");
          }
          if (interval) clearInterval(interval);
        })
        .finally(() => {
          setDataLoaded(true);
        });
    }
    fetchData();
    if (interval) clearInterval(interval);
    interval = setInterval(() => {
      fetchData();
    }, 60000);
  }, [inputValues]);


    useEffect(() => {
        if (tracking_number) {
            Sentry.setUser({ id: tracking_number });
        }
    }, [tracking_number]);


  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
        <Sentry.ErrorBoundary
            fallback={fallbackProps => <ErrorBoundaryFallback {...fallbackProps} />}
        >
          <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            {(!screenSm || firstPage) && (
              <Header
                firstPage={firstPage}
                language={language}
                changeLanguage={changeLanguage}
              />
            )}
            {firstPage ? (
              <FirstPage />
            ) : (
              <Content
                data={data}
                error={error}
                tooltipOpen={tooltipOpen}
                setTooltipOpen={setTooltipOpen}
                tracking_number={tracking_number}
                postal_code={postal_code}
                dataLoaded={dataLoaded}
                postalCode={inputValues.postalCode}
                postalAvailable={postalAvailable}
                setInputValues={setInputValues}
              />
            )}
            <Box
              sx={{
                flexGrow: 1,
                background: firstPage ? "#fff" : "none",
                marginBottom: firstPage && !screenSm ? "-3px" : 0
              }}
            ></Box>
            <Footer language={language} changeLanguage={changeLanguage} />
          </Box>
        </Sentry.ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
